import React, { useState, useEffect } from 'react';
import { 
  GraduationCap, BookOpen, Clock, CheckCircle, XCircle, 
  Users, Play, RefreshCw, Award, ChevronRight, AlertCircle,
  Shield, UserCheck, Briefcase, Settings, Plus, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_INFO = {
  compliance: { label: 'Compliance', color: 'bg-red-500', icon: Shield },
  customer_relations: { label: 'Customer Relations', color: 'bg-blue-500', icon: Users },
  people: { label: 'People Team', color: 'bg-purple-500', icon: UserCheck },
  operations: { label: 'Operations', color: 'bg-green-500', icon: Settings },
  general: { label: 'General', color: 'bg-gray-500', icon: BookOpen }
};

const TrainingPortalPage = ({ getAuthHeaders, currentUser }) => {
  const [modules, setModules] = useState([]);
  const [myAssignments, setMyAssignments] = useState([]);
  const [teamAssignments, setTeamAssignments] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [viewMode, setViewMode] = useState('my-training');
  const [selectedModule, setSelectedModule] = useState(null);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showExamModal, setShowExamModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [examAnswers, setExamAnswers] = useState([]);
  const [examResult, setExamResult] = useState(null);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [assignForm, setAssignForm] = useState({
    staff_username: '',
    module_id: '',
    due_date: '',
    notes: ''
  });

  const isManager = currentUser?.role === 'manager';
  const isExecutive = ['superadmin', 'executive', 'admin'].includes(currentUser?.role);
  const canManageTraining = isManager || isExecutive;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const [modulesRes, assignmentsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/training/modules`, { headers }),
        axios.get(`${BACKEND_URL}/api/training/my-assignments`, { headers })
      ]);
      setModules(modulesRes.data);
      setMyAssignments(assignmentsRes.data);

      if (canManageTraining) {
        const [teamRes, staffRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/training/team-assignments`, { headers }),
          axios.get(`${BACKEND_URL}/api/training/staff-list`, { headers })
        ]);
        setTeamAssignments(teamRes.data);
        setStaffList(staffRes.data);
      }
    } catch (error) {
      console.error('Error fetching training data:', error);
    }
  };

  const openModule = async (moduleId, assignment = null) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${BACKEND_URL}/api/training/modules/${moduleId}`, { headers });
      setSelectedModule(response.data);
      setCurrentAssignment(assignment);
      setShowModuleModal(true);
    } catch (error) {
      toast.error('Failed to load module');
    }
  };

  const startExam = () => {
    if (!selectedModule?.questions?.length) {
      toast.error('No questions available for this module');
      return;
    }
    setExamAnswers(new Array(selectedModule.questions.length).fill(-1));
    setExamResult(null);
    setShowModuleModal(false);
    setShowExamModal(true);
  };

  const submitExam = async () => {
    if (!currentAssignment) {
      toast.error('No assignment found');
      return;
    }

    if (examAnswers.includes(-1)) {
      toast.error('Please answer all questions');
      return;
    }

    try {
      const headers = getAuthHeaders();
      const response = await axios.post(
        `${BACKEND_URL}/api/training/submit-exam/${currentAssignment.id}`,
        { answers: examAnswers },
        { headers }
      );
      setExamResult(response.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit exam');
    }
  };

  const handleAssign = async () => {
    if (!assignForm.staff_username || !assignForm.module_id) {
      toast.error('Please select staff and module');
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/training/assign`, assignForm, { headers });
      toast.success('Training assigned successfully');
      setShowAssignModal(false);
      setAssignForm({ staff_username: '', module_id: '', due_date: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign training');
    }
  };

  const deleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/training/assignments/${assignmentId}`, { headers });
      toast.success('Assignment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete assignment');
    }
  };

  const getCategoryInfo = (category) => CATEGORY_INFO[category] || CATEGORY_INFO.general;

  const getStatusBadge = (status, passed) => {
    if (passed) return { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Passed' };
    switch (status) {
      case 'completed': return { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Completed' };
      case 'in_progress': return { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'In Progress' };
      default: return { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', label: 'Pending' };
    }
  };

  const pendingCount = myAssignments.filter(a => !a.passed).length;
  const completedCount = myAssignments.filter(a => a.passed).length;

  return (
    <div className="space-y-6" data-testid="training-portal-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Training Portal</h1>
          <p className="text-gray-400">Complete your assigned training and earn certifications</p>
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 px-3 py-1">
              {pendingCount} pending
            </Badge>
          )}
          {canManageTraining && (
            <Button
              onClick={() => setShowAssignModal(true)}
              className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white"
              data-testid="assign-training-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Assign Training
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-gray-900 to-black border-cyan-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{myAssignments.length}</p>
                <p className="text-xs text-gray-400">Total Assigned</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-gray-900 to-black border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{completedCount}</p>
                <p className="text-xs text-gray-400">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{pendingCount}</p>
                <p className="text-xs text-gray-400">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-gray-900 to-black border-purple-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Award className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {myAssignments.length > 0 
                    ? Math.round((completedCount / myAssignments.length) * 100) 
                    : 0}%
                </p>
                <p className="text-xs text-gray-400">Completion Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setViewMode('my-training')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            viewMode === 'my-training' ? 'bg-cyan-500 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          My Training
          {pendingCount > 0 && (
            <span className="bg-yellow-500 text-white text-xs px-1.5 py-0.5 rounded-full">{pendingCount}</span>
          )}
        </button>
        <button
          onClick={() => setViewMode('catalog')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            viewMode === 'catalog' ? 'bg-cyan-500 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          <GraduationCap className="w-4 h-4" />
          Training Catalog
        </button>
        {canManageTraining && (
          <button
            onClick={() => setViewMode('team')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              viewMode === 'team' ? 'bg-cyan-500 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Users className="w-4 h-4" />
            Team Progress
          </button>
        )}
      </div>

      {/* My Training View */}
      {viewMode === 'my-training' && (
        <div className="space-y-4">
          {myAssignments.length === 0 ? (
            <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-700">
              <CardContent className="p-8 text-center">
                <GraduationCap className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                <p className="text-gray-400">No training assigned yet</p>
                <p className="text-gray-500 text-sm mt-2">Check the training catalog to see available courses</p>
              </CardContent>
            </Card>
          ) : (
            myAssignments.map(assignment => {
              const catInfo = getCategoryInfo(assignment.module_category);
              const statusInfo = getStatusBadge(assignment.status, assignment.passed);
              const CatIcon = catInfo.icon;

              return (
                <Card 
                  key={assignment.id}
                  className={`bg-gradient-to-br from-gray-900 to-black border-gray-700 hover:border-cyan-500/50 transition-all ${
                    !assignment.passed ? 'border-l-4 border-l-yellow-500' : 'border-l-4 border-l-green-500'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 ${catInfo.color}/20 rounded-xl flex items-center justify-center`}>
                          <CatIcon className={`w-6 h-6 ${catInfo.color.replace('bg-', 'text-').replace('-500', '-400')}`} />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold mb-1">{assignment.module_title}</h3>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={`${catInfo.color}/20 ${catInfo.color.replace('bg-', 'text-').replace('-500', '-400')} border-${catInfo.color.split('-')[1]}-500/30`}>
                              {catInfo.label}
                            </Badge>
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                          </div>
                          <p className="text-gray-500 text-sm">
                            Assigned by {assignment.assigned_by_name} • 
                            {assignment.attempts_count > 0 ? ` ${assignment.attempts_count} attempt${assignment.attempts_count > 1 ? 's' : ''}` : ' Not started'}
                            {assignment.latest_score !== null && ` • Best: ${assignment.latest_score}%`}
                          </p>
                          {assignment.due_date && (
                            <p className="text-yellow-400 text-sm mt-1">
                              Due: {new Date(assignment.due_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => openModule(assignment.module_id, assignment)}
                        className={assignment.passed 
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                        }
                      >
                        {assignment.passed ? (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Review
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            {assignment.attempts_count > 0 ? 'Retry' : 'Start'}
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Training Catalog View */}
      {viewMode === 'catalog' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map(module => {
            const catInfo = getCategoryInfo(module.category);
            const CatIcon = catInfo.icon;

            return (
              <Card 
                key={module.id}
                className="bg-gradient-to-br from-gray-900 to-black border-gray-700 hover:border-cyan-500/50 transition-all cursor-pointer"
                onClick={() => openModule(module.id)}
              >
                <CardContent className="p-4">
                  <div className={`w-12 h-12 ${catInfo.color}/20 rounded-xl flex items-center justify-center mb-3`}>
                    <CatIcon className={`w-6 h-6 ${catInfo.color.replace('bg-', 'text-').replace('-500', '-400')}`} />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{module.title}</h3>
                  <p className="text-gray-400 text-sm mb-3 line-clamp-2">{module.description}</p>
                  <div className="flex items-center justify-between">
                    <Badge className={`${catInfo.color}/20 ${catInfo.color.replace('bg-', 'text-').replace('-500', '-400')}`}>
                      {catInfo.label}
                    </Badge>
                    <span className="text-gray-500 text-sm flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {module.duration_minutes} min
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Team Progress View */}
      {viewMode === 'team' && canManageTraining && (
        <Card className="bg-gradient-to-br from-gray-900 to-black border-cyan-500/20">
          <CardHeader className="border-b border-gray-800">
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Team Training Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {teamAssignments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No training assignments yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {teamAssignments.map(assignment => {
                  const statusInfo = getStatusBadge(assignment.status, assignment.passed);

                  return (
                    <div key={assignment.id} className="p-4 rounded-xl bg-black/40 border border-gray-800">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-medium">{assignment.staff_display_name}</p>
                          <p className="text-gray-400 text-sm">{assignment.module_title}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                            {assignment.latest_score !== null && (
                              <span className="text-gray-500 text-sm">Score: {assignment.latest_score}%</span>
                            )}
                            <span className="text-gray-500 text-sm">
                              {assignment.attempts_count} attempt{assignment.attempts_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteAssignment(assignment.id)}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Module Content Modal */}
      <Dialog open={showModuleModal} onOpenChange={setShowModuleModal}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-cyan-400" />
              {selectedModule?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedModule && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-black/40 rounded-xl">
                <p className="text-gray-400 text-sm mb-2">{selectedModule.description}</p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {selectedModule.duration_minutes} minutes
                  </span>
                  <span className="text-gray-500">
                    Passing score: {selectedModule.passing_score}%
                  </span>
                  <span className="text-gray-500">
                    {selectedModule.questions?.length || 0} questions
                  </span>
                </div>
              </div>

              <div className="prose prose-invert max-w-none">
                <div className="text-gray-300 whitespace-pre-wrap text-sm leading-relaxed bg-black/20 p-4 rounded-xl">
                  {selectedModule.content}
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowModuleModal(false)}>Close</Button>
                {currentAssignment && (
                  <Button onClick={startExam} className="bg-cyan-500 hover:bg-cyan-600">
                    <Play className="w-4 h-4 mr-2" />
                    {currentAssignment.passed ? 'Retake Exam' : 'Start Exam'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Exam Modal */}
      <Dialog open={showExamModal} onOpenChange={setShowExamModal}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-cyan-400" />
              {selectedModule?.title} - Exam
            </DialogTitle>
          </DialogHeader>
          {selectedModule && !examResult && (
            <div className="space-y-6 mt-4">
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 text-sm">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  Passing score: {selectedModule.passing_score}% • If you fail, you can retake the exam.
                </p>
              </div>

              {selectedModule.questions?.map((q, qIndex) => (
                <div key={qIndex} className="p-4 bg-black/40 rounded-xl">
                  <p className="text-white font-medium mb-3">
                    {qIndex + 1}. {q.question}
                  </p>
                  <div className="space-y-2">
                    {q.options.map((option, oIndex) => (
                      <button
                        key={oIndex}
                        onClick={() => {
                          const newAnswers = [...examAnswers];
                          newAnswers[qIndex] = oIndex;
                          setExamAnswers(newAnswers);
                        }}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          examAnswers[qIndex] === oIndex
                            ? 'border-cyan-500 bg-cyan-500/20 text-white'
                            : 'border-gray-700 hover:border-gray-600 text-gray-300'
                        }`}
                      >
                        <span className="mr-2 text-gray-500">{String.fromCharCode(65 + oIndex)}.</span>
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowExamModal(false)}>Cancel</Button>
                <Button onClick={submitExam} className="bg-cyan-500 hover:bg-cyan-600">
                  Submit Exam
                </Button>
              </div>
            </div>
          )}

          {examResult && (
            <div className="space-y-6 mt-4">
              <div className={`text-center p-6 rounded-xl ${
                examResult.passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
              }`}>
                {examResult.passed ? (
                  <>
                    <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-green-400 mb-2">Congratulations!</h3>
                    <p className="text-gray-300">You passed the exam</p>
                  </>
                ) : (
                  <>
                    <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-red-400 mb-2">Not Passed</h3>
                    <p className="text-gray-300">Please review the material and try again</p>
                  </>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-black/40 rounded-lg">
                  <p className="text-2xl font-bold text-white">{examResult.score}%</p>
                  <p className="text-gray-500 text-sm">Your Score</p>
                </div>
                <div className="p-3 bg-black/40 rounded-lg">
                  <p className="text-2xl font-bold text-white">{examResult.passing_score}%</p>
                  <p className="text-gray-500 text-sm">Passing Score</p>
                </div>
                <div className="p-3 bg-black/40 rounded-lg">
                  <p className="text-2xl font-bold text-white">{examResult.correct_count}/{examResult.total_questions}</p>
                  <p className="text-gray-500 text-sm">Correct Answers</p>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => {
                  setShowExamModal(false);
                  setExamResult(null);
                }}>
                  Close
                </Button>
                {!examResult.passed && (
                  <Button onClick={() => {
                    setExamResult(null);
                    setExamAnswers(new Array(selectedModule.questions.length).fill(-1));
                  }} className="bg-yellow-500 hover:bg-yellow-600">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Training Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-purple-400" />
              Assign Training
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Staff Member *</label>
              <select
                value={assignForm.staff_username}
                onChange={(e) => setAssignForm({ ...assignForm, staff_username: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
              >
                <option value="">Select staff member</option>
                {staffList.map(staff => (
                  <option key={staff.username} value={staff.username}>
                    {staff.display_name || staff.username} ({staff.role})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Training Module *</label>
              <select
                value={assignForm.module_id}
                onChange={(e) => setAssignForm({ ...assignForm, module_id: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
              >
                <option value="">Select module</option>
                {modules.map(module => (
                  <option key={module.id} value={module.id}>
                    {module.title} ({getCategoryInfo(module.category).label})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Due Date (optional)</label>
              <input
                type="date"
                value={assignForm.due_date}
                onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Notes (optional)</label>
              <textarea
                value={assignForm.notes}
                onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
              <Button onClick={handleAssign} className="bg-purple-500 hover:bg-purple-600">
                Assign Training
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainingPortalPage;
